/**
 * TrendBarChart · Module A 投放趋势对比 - 柱状图
 *
 * 需求 §4.4.4（参考 figma node 3270-41219）：
 * - 每个 X 轴日期对应一组柱（每个游戏一根并排柱）
 * - 配色 chartAllColors
 * - tooltip / legend 与折线图统一（带游戏 icon）
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_TM, DBO_V } from '@op/utils/format';
import { ITrendSeries } from '../../types/trend';
import { TDateType } from '../../types/common';
import { TGameMap } from '../../hooks/useGameMap';
import { useGameColorContext } from '../../context/GameColorContext';
import {
  buildEchartSeries,
  buildGameRich,
  buildXAxisData,
  gameRichLabel,
  gameTooltipLabel,
} from './utils';

export interface ITrendBarChartProps {
  series: ITrendSeries[];
  /** 游戏元信息映射（cond.materials_config_games） */
  gameMap: TGameMap;
  /** 时间粒度（用于 tooltip 标题格式化） */
  dateType: TDateType;
  /** 图表高度 */
  height?: number;
  emptyTitleText: string;
}

export default function TrendBarChart({
  series,
  gameMap,
  dateType,
  height = 360,
  emptyTitleText
}: ITrendBarChartProps) {

  const { gameColorMap } = useGameColorContext();

  const empty = useMemo(() => {
    return !series?.length || series.every((item) => !item.values?.length);
  }, [series]);

  const options = useMemo(() => {
    const xAxisData = buildXAxisData(series);
    const echartSeries = buildEchartSeries(series, 'bar', gameColorMap);
    const gameRich = buildGameRich(series, gameMap, 16);

    // 图例顺序：按第一个时间点的游戏值从大到小排列
    const firstTimeValues = new Map<string, number>();
    series.forEach((s) => {
      const firstValue = s.values?.[0]?.value;
      if (firstValue !== undefined) {
        firstTimeValues.set(s.game_id, firstValue);
      }
    });
    const legendData = series
      .map((s) => s.game_id)
      .sort((a, b) => (firstTimeValues.get(b) || 0) - (firstTimeValues.get(a) || 0));

    // 柱状图 series 按相同顺序重排，使柱子从大到小展示
    const sortedEchartSeries = [...echartSeries].sort(
      (a: any, b: any) => (firstTimeValues.get(b.name) || 0) - (firstTimeValues.get(a.name) || 0),
    );

    return {
      legend: {
        data: legendData,
        formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
        textStyle: {
          rich: gameRich,
        },
      },
      tooltip: {
        formatter: (params: any) =>
          formatChartParamsDate({
            title: DBO_EC.toolTime(params?.[0]?.axisValue, dateType),
            params,
            isSort: true,
            listTitleFunc: (item: any) =>
              gameTooltipLabel(item.seriesName, gameMap),
            valueFormat: (val: number) =>
              Number.isFinite(val) ? DBO_V.reviews(val) : '-',
          }),
      },
      xAxis: {
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) => DBO_EC.timeX(value, dateType),
        },
      },
      yAxis: {
        axisLabel: {
          formatter: DBO_EC.reviews,
        },
      },
      axisPointer: {
        label: {
          formatter: ({ value }: { value: number | string; }) => {
            if (dateType === 'monthly') {
              return DBO_EC.timeX(String(value), dateType);
            }
            return value;
          },
        }
      },
      series: sortedEchartSeries,
    };
  }, [series, gameMap, dateType, gameColorMap]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: 'bar' }}
      height={height}
      emptyTitleText={emptyTitleText}
      hasEmptyDesc={false}
      empty={empty}
    />
  );
}
